import _ from "underscore";
import DriveView from "../../DriveView";
import ManageShipmentsView from "../assets/manageShipments";
import { Dialog } from "../framework/dialog";
import { getCurrentVehicle } from "../../utils/device";
import { Shipment, IShipment } from "../../model/Shipment";
import Sync from "../../sync/sync";
import { getServerDate } from "../../utils/storage/date";
import { show, hide } from "../../utils/vanilla";
import { PageLevel } from "../../store/mobile/types";
import constants from "../../constants";
import { SyncType } from "../../sync/sync.types";
import DetachAllPanelView from "./detachAllHelper";
import { getShipmentsFromState, setShipmentsInState } from "./helper";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import idSerializer from "geotab-uuid";
import html from "./selectShipment.tmpl";
import PerformanceProfilerFactory from "../../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../../utils/performanceProfiler/measureName";

    export default class SelectShipmentsView extends DriveView<Backbone.Model> {

        protected strings = [
            "Add",
            "Saving in progress",
            "You have more than {0} shipments attached. Only {0} shipments can be displayed. Please detach individual shipments in order to load more assets.",
            "You cannot attach more than {0} shipments. Please detach existing shipments in order to add new assets.",
            "Are you sure you want to detach {0} shipments?"
        ];
        private readonly template = _.template(html);
        private readonly sync: Sync;
        private readonly workflow: boolean;
        private readonly manageShipmentsView: ManageShipmentsView;
        private readonly scrollContainer = document.getElementById("scroll-content");
        private readonly detachAllView: DetachAllPanelView;

        constructor (options) {
            super(options, {
                title: "Attach Shipments",
                pageLevel: options.pageLevel || PageLevel.TWO
            });
            this.sync = this.app.sync;
            this.workflow = options.workflow || false;
            this.manageShipmentsView = new ManageShipmentsView({ app: this.app, workflow: this.workflow });
            this.listenTo(this.manageShipmentsView, "shipmentRemoved", () => this.shouldBlockAddingShipments());

            this.detachAllView = new DetachAllPanelView({
                getConfirmMessage: () => this.getConfirmMessage(),
                getDetachErrorMessage: () => this.getDetachErrorMessage(),
                detachCallback: () => this.detachAll(),
                detachButtonText: "Detach all shipments",
                app: this.app
            });
        }

        /* eslint-disable @typescript-eslint/unbound-method */
        events() {
            return {
                "click #add": this.add,
                "click #shipment-continue-button": this.continueToNext
            } as Backbone.EventsHash;
        }
        /* eslint-enable @typescript-eslint/unbound-method */

        render() {
            void this.display(this.template())
                .then(() => {
                    let shipmentListElement = document.getElementById("manageShipmentsList");
                    shipmentListElement && this.manageShipmentsView.setElement(shipmentListElement).render();
                    this.shouldBlockAddingShipments();
                });
            return this;
        }

        remove () {
            this.manageShipmentsView?.remove();
            this.detachAllView?.remove();
            super.remove();
            return this;
        }

        private getActiveShipments() {
            return this.manageShipmentsView.getCurrentShipments();
        }

        private shouldBlockAddingShipments(): boolean {
            let shipmentCount = this.getActiveShipments().length;
            let atLimit = shipmentCount >= constants.ASSET_LIMITS.MAX_SHIPMENTS_LIMIT;
            let assetDetachShipments = document.getElementById("select-asset-detach-shipments") as HTMLElement;
            let addPanel = document.getElementById("add-shipments-panel") as HTMLElement;
            if (atLimit) {
                if (assetDetachShipments && this.detachAllView) {
                    this.detachAllView.setElement(assetDetachShipments);
                    this.detachAllView.show(shipmentCount > constants.ASSET_LIMITS.MAX_SHIPMENTS_LIMIT);
                    assetDetachShipments && show(assetDetachShipments);
                }
                addPanel && hide(addPanel);
            } else {
                assetDetachShipments && hide(assetDetachShipments);
                addPanel && show(addPanel);
            }
            return atLimit;
        }

        private getConfirmMessage() {
            return this.getString("Are you sure you want to detach {0} shipments?").replace("{0}", this.getActiveShipments().length.toString());
        }

        private getDetachErrorMessage() {
            let shipmentCount = this.getActiveShipments().length;
            return (shipmentCount > constants.ASSET_LIMITS.MAX_SHIPMENTS_LIMIT
                ? this.getString("You have more than {0} shipments attached. Only {0} shipments can be displayed. Please detach individual shipments in order to load more assets.")
                : this.getString("You cannot attach more than {0} shipments. Please detach existing shipments in order to add new assets."))
                .replace(/{(\d+)}/g, constants.ASSET_LIMITS.MAX_SHIPMENTS_LIMIT.toString());
        }

        private detachAll() {
            let shipments = this.getActiveShipments();
            return Promise.all(shipments.map(shipment => {
                    shipment.detach();
                    if (!shipment._isTemp) {
                        return this.sync.shipments.setItem(shipment, true);
                    }
                    return Promise.resolve();
                }))
                .then(() => {
                    setShipmentsInState(this.app.store, [], this.app.userService);
                    this.manageShipmentsView.refresh(false);
                    !this.workflow && this.sync.synchronize(false, null, [SyncType.Shipments]);
                    this.render();
                });
        }

        private addShipment(shipment: Shipment) {
            let selectShipmentProfiler = PerformanceProfilerFactory.getInstance(MeasureName.AddShipment);
            if (!this.workflow) {
                return selectShipmentProfiler.measureAsync(() => this.sync.shipments.addItem(shipment, this.workflow));
            } else {
                selectShipmentProfiler.start();
                // Add to temp storage instead of main trailer storage
                let tempShipments = getShipmentsFromState(this.app.store, this.app.userService);
                tempShipments.push(<IShipment>shipment);
                setShipmentsInState(this.app.store, tempShipments, this.app.userService);
                selectShipmentProfiler.end();
                return Promise.resolve();
            }
        }

        private add() {
            if (this.shouldBlockAddingShipments()) {
                return;
            }

            let nameElement = <HTMLInputElement>document.getElementById("shipperName");
            let shipperName = nameElement && nameElement.value.trim();

            let numberElement = <HTMLInputElement>document.getElementById("documentNumber");
            let documentNumber = numberElement && numberElement.value.trim();

            let commodityElement = <HTMLInputElement>document.getElementById("commodity");
            let commodity = commodityElement && commodityElement.value.trim();

            if (documentNumber || (shipperName && commodity)) {
                let currentUser = this.app.userService.getCurrentUser();
                let currentVehicle = getCurrentVehicle();
                let now = getServerDate();
                let shipment = new Shipment({
                    id: idSerializer.get(),
                    device: currentVehicle && currentVehicle.id ? { id: currentVehicle.id } : { id: "NoDeviceId" },
                    driver: currentUser ? { id: currentUser.id } : { id: "NoUserId"},
                    shipperName,
                    documentNumber,
                    commodity,
                    dateTime: now.toISOString(),
                    activeFrom: now.toISOString()
                });

                let addBtn = <HTMLInputElement>document.getElementById("add");
                if (addBtn) {
                    addBtn.disabled = true;
                    addBtn.innerHTML = this.getString("Saving in progress");
                }

                void this.addShipment(shipment).then(() => {
                    this.manageShipmentsView.refresh();
                    nameElement.value = "";
                    numberElement.value = "";
                    commodityElement.value = "";

                    if (addBtn) {
                        addBtn.innerHTML = this.getString("Add");
                        addBtn.disabled = false;
                    }

                    if (this.scrollContainer) {
                        this.scrollContainer.scrollTop = 0;
                    }
                    this.shouldBlockAddingShipments();
                });
            } else {
                let shipmentWarningDialog = new Dialog(
                    "shipment-missing-data",
                    "Shipments must have a document number or both a commodity and a shipper name.",
                    this.translator,
                    "Warning",
                    [[
                        {
                            text: "Ok",
                            id: "button-ok",
                            callback: dialog => dialog.remove()
                        }
                    ]],
                    { isNightMode: this.app.store.getState().data.nightMode }
                );
                void shipmentWarningDialog.open();
            }
        }

        private continueToNext () {
            if (this.workflow) {
                dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
            } else {
                this.app.router.navigate("assets/main", true);
            }
        }
    }